import { getCookie } from '@agendapro/sapphire';
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { camelizeKeys, decamelizeKeys } from 'humps';

import { ENV } from '@/config/env';

export const AP_API =
  process.env.NODE_ENV === 'test'
    ? axios
    : axios.create({
        baseURL: ENV.AP_API_URL,
        timeout: 60000,
        headers: {
          'Content-Type': 'application/json',
        },
      });

AP_API.interceptors.request.use(
  (config) => {
    const newConfig = { ...config };

    // El JWT de Cognito (`ap_cognito_authorization`) se renueva por su cuenta (~1h) en el
    // dominio compartido. Se lee por request (no en import-time) para no mandar nunca un
    // token vencido; sin cookie, no se agrega Authorization y el microservicio responde 401.
    if (typeof window !== 'undefined' && newConfig.headers) {
      const authToken = getCookie('ap_cognito_authorization');

      if (authToken) {
        newConfig.headers.Authorization = authToken;
      }
    }

    if (newConfig.headers && newConfig.headers['Content-Type'] === 'multipart/form-data') {
      return newConfig;
    }
    if (config.params) {
      newConfig.params = decamelizeKeys(config.params);
    }
    if (config.data) {
      newConfig.data = decamelizeKeys(config.data);
    }
    return newConfig;
  },
  (error) => Promise.reject(error),
);

AP_API.interceptors.response.use(
  (response) => {
    if (response.data && response.headers['content-type'].includes('application/json')) {
      response.data = camelizeKeys(response.data);
    }

    return response;
  },
  (error) => Promise.reject(error),
);

type SsrRequestConfig = InternalAxiosRequestConfig & { __ssrStart?: number; __retried?: boolean };

const BFF_PATH_PREFIX = '/marketplace-minisite-bff';

const isServerSide = () => typeof window === 'undefined';

const logBffSsrCall = (config: SsrRequestConfig | undefined, status: number | undefined, reusedSocket: boolean) => {
  if (!isServerSide() || !config?.url?.startsWith(BFF_PATH_PREFIX)) {
    return;
  }

  const [path] = config.url.split('?');

  // eslint-disable-next-line no-console
  console.log(
    JSON.stringify({
      action: 'BFF_SSR_CALL',
      path,
      status,
      ms: config.__ssrStart !== undefined ? Date.now() - config.__ssrStart : undefined,
      reusedSocket,
    }),
  );
};

export const onSsrRequest = (config: SsrRequestConfig): SsrRequestConfig =>
  isServerSide() ? { ...config, __ssrStart: Date.now() } : config;

export const onSsrResponse = (response: AxiosResponse): AxiosResponse => {
  logBffSsrCall(response.config, response.status, Boolean(response.request?.reusedSocket));

  return response;
};

export const onSsrResponseError = (error: AxiosError) => {
  const config = error.config as SsrRequestConfig | undefined;

  logBffSsrCall(config, error.response?.status, Boolean(error.request?.reusedSocket));

  if (isServerSide() && config?.method === 'get' && error.code === 'ECONNRESET' && !config.__retried) {
    const retryConfig: SsrRequestConfig = { ...config, __retried: true };

    return AP_API.request(retryConfig);
  }

  return Promise.reject(error);
};

AP_API.interceptors.request.use(onSsrRequest);
AP_API.interceptors.response.use(onSsrResponse, onSsrResponseError);
